import { zCorpInvoiceDom, zCorpInvoiceLineDom, zCorpLedgerEntryDom, zCorpPaymentDom, } from "@/types/company/settlements.domain";
import { zAdjustmentResultDto, zBillingRunResultDto, zCorpInvoiceLineRowDto, zCorpInvoiceRowDto, zCorpLedgerRowDto, zCorpPaymentRowDto, zInvoiceExternalDocResultDto, zInvoiceVoidResultDto, zOrderAdjustResultDto, zPaymentRegisterResultDto, zPaymentVoidResultDto, } from "@/types/company/settlements.dto";
export const CompanySettlementsMapper = {
    /** Строка `v_corp_invoices`; у VOID `paid_amount`/`payment_state` = null (CCR-033). */
    invoiceToDomain(raw) {
        const row = zCorpInvoiceRowDto.parse(raw);
        return zCorpInvoiceDom.parse({
            invoiceId: row.invoice_id,
            companyId: row.company_id,
            number: row.number,
            periodStart: row.period_start,
            periodEnd: row.period_end,
            issueDate: row.issue_date,
            dueDate: row.due_date,
            total: row.total,
            ordersCount: row.orders_count,
            totalKwh: row.total_kwh,
            status: row.status,
            externalDocNumber: row.external_doc_number ?? null,
            externalDocDate: row.external_doc_date ?? null,
            paidAmount: row.paid_amount,
            paymentState: row.payment_state,
            isOverdue: row.is_overdue,
            daysOverdue: row.days_overdue ?? null,
            runId: row.run_id ?? null,
            voidedAt: row.voided_at ?? null,
            voidedBy: row.voided_by ?? null,
            voidReason: row.void_reason ?? null,
        });
    },
    /** Строка `v_corp_invoice_lines`. */
    lineToDomain(raw) {
        const row = zCorpInvoiceLineRowDto.parse(raw);
        return zCorpInvoiceLineDom.parse({
            lineId: row.line_id,
            invoiceId: row.invoice_id,
            companyId: row.company_id,
            orderId: row.order_id ?? null,
            orderNumber: row.order_number ?? null,
            lineDate: row.line_date,
            description: row.description ?? null,
            employeeLabel: row.employee_label ?? null,
            kwh: row.kwh ?? null,
            pricePerKwh: row.price_per_kwh ?? null,
            serviceFee: row.service_fee ?? null,
            amount: row.amount,
        });
    },
    /** Строка `v_corp_payments`. */
    paymentToDomain(raw) {
        const row = zCorpPaymentRowDto.parse(raw);
        return zCorpPaymentDom.parse({
            paymentId: row.payment_id,
            companyId: row.company_id,
            amount: row.amount,
            paidAt: row.paid_at,
            docNumber: row.doc_number ?? null,
            docDate: row.doc_date ?? null,
            comment: row.comment ?? null,
            isVoided: row.is_voided,
            voidedAt: row.voided_at ?? null,
            createdAt: row.created_at,
            createdBy: row.created_by ?? null,
            voidedBy: row.voided_by ?? null,
            voidReason: row.void_reason ?? null,
        });
    },
    /** Строка `v_corp_ledger`; `reversesEntryId`/`createdByKind` — опциональны до WP-DB-05 (CCR-029). */
    ledgerToDomain(raw) {
        const row = zCorpLedgerRowDto.parse(raw);
        return zCorpLedgerEntryDom.parse({
            entryId: row.entry_id,
            companyId: row.company_id,
            entryType: row.entry_type,
            entryClass: row.entry_class,
            amount: row.amount,
            effectiveAt: row.effective_at,
            description: row.description ?? null,
            orderId: row.order_id ?? null,
            orderNumber: row.order_number ?? null,
            paymentId: row.payment_id ?? null,
            invoiceId: row.invoice_id ?? null,
            invoiceNumber: row.invoice_number ?? null,
            createdAt: row.created_at,
            createdBy: row.created_by ?? null,
            reversesEntryId: row.reverses_entry_id ?? null,
            createdByKind: row.created_by_kind ?? null,
        });
    },
    /** Параметры RPC `corp_payment_register` (§4.3). */
    toPaymentRegisterParams(companyId, input, idempotencyKey) {
        return {
            p_company_id: companyId,
            p_amount: input.amount,
            p_paid_at: input.paidAt,
            p_doc_number: input.docNumber,
            p_doc_date: input.docDate,
            p_comment: input.comment,
            p_idempotency_key: idempotencyKey,
        };
    },
    /** Параметры RPC `corp_adjustment_create` (§4.3). */
    toAdjustmentParams(companyId, input, idempotencyKey) {
        return {
            p_company_id: companyId,
            p_amount: input.amount,
            p_description: input.description,
            p_idempotency_key: idempotencyKey,
        };
    },
    /** Параметры RPC `corp_invoice_set_external_doc` (§4.3). */
    toExternalDocParams(invoiceId, input) {
        return {
            p_invoice_id: invoiceId,
            p_doc_number: input.docNumber,
            p_doc_date: input.docDate,
        };
    },
    paymentRegisterResultToDomain(raw) {
        const dto = zPaymentRegisterResultDto.parse(raw);
        return { paymentId: dto.paymentId, ledgerEntryId: dto.ledgerEntryId, balance: dto.balance };
    },
    paymentVoidResultToDomain(raw) {
        const dto = zPaymentVoidResultDto.parse(raw);
        return { paymentId: dto.paymentId, reversalEntryId: dto.reversalEntryId, balance: dto.balance };
    },
    adjustmentResultToDomain(raw) {
        const dto = zAdjustmentResultDto.parse(raw);
        return { ledgerEntryId: dto.ledgerEntryId, balance: dto.balance };
    },
    orderAdjustResultToDomain(raw) {
        const dto = zOrderAdjustResultDto.parse(raw);
        return { orderId: dto.orderId, oldAmount: dto.oldAmount, newAmount: dto.newAmount, balance: dto.balance };
    },
    /** Ответ `corp_generate_invoices` (§4.3). */
    runResultToDomain(raw) {
        const dto = zBillingRunResultDto.parse(raw);
        return {
            runId: dto.runId,
            asOf: dto.asOf,
            companiesChecked: dto.companiesChecked,
            invoicesCreated: dto.invoicesCreated.map((invoice) => ({
                invoiceId: invoice.invoiceId,
                number: invoice.number,
                companyId: invoice.companyId,
                periodStart: invoice.periodStart,
                periodEnd: invoice.periodEnd,
                total: invoice.total,
            })),
        };
    },
    invoiceVoidResultToDomain(raw) {
        const dto = zInvoiceVoidResultDto.parse(raw);
        return { invoiceId: dto.invoiceId, status: dto.status };
    },
    invoiceExternalDocResultToDomain(raw) {
        const dto = zInvoiceExternalDocResultDto.parse(raw);
        return { invoiceId: dto.invoiceId };
    },
};
